import React, { useState } from 'react';
import { FileText, RefreshCw, Settings } from 'lucide-react';
import { apiFetchReport } from '../../lib/api';

interface ReportSettings {
  morningSummaryUrl?: string;
  dailyIntelUrl?: string;
  selfAuditUrl?: string;
  agentPerformanceUrl?: string;
}

interface ReportsPanelProps {
  settings?: ReportSettings;
}

const TABS = [
  { id: 'morning', label: 'Morning Summary', key: 'morningSummaryUrl' as const },
  { id: 'intel', label: 'Daily Intelligence', key: 'dailyIntelUrl' as const },
  { id: 'audit', label: 'Self-Audit', key: 'selfAuditUrl' as const },
  { id: 'performance', label: 'Agent Performance', key: 'agentPerformanceUrl' as const },
] as const;

type TabId = typeof TABS[number]['id'];

/**
 * ReportsPanel — tabbed read-only markdown/text report viewer.
 * Fetches content from configurable URLs (set in Settings).
 */
export default function ReportsPanel({ settings }: ReportsPanelProps) {
  const [activeTab, setActiveTab] = useState<TabId>('morning');
  const [content, setContent] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});

  const activeTabDef = TABS.find((t) => t.id === activeTab)!;
  const activeUrl = settings?.[activeTabDef.key];
  const activeContent = content[activeTab];

  const loadReport = async (tabId: TabId, url: string) => {
    if (!url) return;
    setLoading((prev) => ({ ...prev, [tabId]: true }));
    try {
      const { content: text } = await apiFetchReport(url);
      setContent((prev) => ({ ...prev, [tabId]: text }));
    } catch {
      setContent((prev) => ({ ...prev, [tabId]: 'Error loading report.' }));
    } finally {
      setLoading((prev) => ({ ...prev, [tabId]: false }));
    }
  };

  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId);
    const tab = TABS.find((t) => t.id === tabId)!;
    const url = settings?.[tab.key];
    if (url && !content[tabId]) {
      loadReport(tabId, url);
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="reports-panel">
      {/* Tabs */}
      <div className="flex border-b border-gray-200 px-4 pt-4 shrink-0">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`report-tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {!activeUrl ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <Settings size={36} className="mb-3 text-gray-300" />
            <p className="text-sm font-medium text-gray-500">Configure endpoint in Settings</p>
            <p className="text-xs text-gray-400 mt-1">
              Set the {activeTabDef.label} URL in Settings → Reports
            </p>
          </div>
        ) : loading[activeTab] ? (
          <div className="flex items-center justify-center h-full">
            <div className="h-6 w-6 rounded-full border-2 border-gray-200 border-t-gray-600 animate-spin" />
          </div>
        ) : activeContent ? (
          <div className="prose prose-sm max-w-none">
            <pre className="whitespace-pre-wrap text-sm text-gray-800 leading-relaxed font-sans">{activeContent}</pre>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full">
            <FileText size={36} className="text-gray-300 mb-3" />
            <p className="text-sm text-gray-500 mb-3">Click to load report</p>
            <button
              onClick={() => loadReport(activeTab, activeUrl)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 transition-colors"
              data-testid="load-report-button"
            >
              <RefreshCw size={14} />
              Load Report
            </button>
          </div>
        )}
      </div>

      {/* Reload button */}
      {activeUrl && activeContent && (
        <div className="border-t border-gray-100 px-6 py-3 flex items-center justify-between bg-gray-50 shrink-0">
          <p className="text-[10px] text-gray-400 truncate max-w-md">{activeUrl}</p>
          <button
            onClick={() => loadReport(activeTab, activeUrl)}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
            data-testid="reload-report-button"
          >
            <RefreshCw size={12} />
            Reload
          </button>
        </div>
      )}
    </div>
  );
}
